import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface CorrMetric {
  symbol: string;
  btc_correlation_30d: number;
  btc_relative_strength_7d: number;
  diversification_value: string;
}

interface CorrMap {
  bySymbol: Record<string, CorrMetric>;
  cachedAt: string | null;
}

async function fetchCorrelations(): Promise<CorrMap> {
  // Read from cached_advanced_metrics directly to avoid hitting CG.
  const { data, error } = await supabase
    .from("cached_advanced_metrics")
    .select("metrics_data, calculated_at")
    .order("calculated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data?.metrics_data) {
    return { bySymbol: {}, cachedAt: null };
  }

  const payload: any = data.metrics_data;
  const metrics: any[] = Array.isArray(payload?.metrics) ? payload.metrics : [];
  const bySymbol: Record<string, CorrMetric> = {};
  for (const m of metrics) {
    if (!m?.symbol) continue;
    bySymbol[String(m.symbol).toUpperCase()] = {
      symbol: String(m.symbol).toUpperCase(),
      btc_correlation_30d: Number(m.btc_correlation_30d ?? 0),
      btc_relative_strength_7d: Number(m.btc_relative_strength_7d ?? 0),
      diversification_value: String(m.diversification_value ?? "MEDIUM"),
    };
  }
  return { bySymbol, cachedAt: data.calculated_at };
}

export function useBtcCorrelations() {
  return useQuery({
    queryKey: ["btc-correlations"],
    queryFn: fetchCorrelations,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useBtcCorrelation(symbol?: string) {
  const q = useBtcCorrelations();
  const key = (symbol || "").toUpperCase();
  const metric = key ? q.data?.bySymbol[key] : undefined;
  return { ...q, metric };
}
